import _defineProperty from "/app/node_modules/.pnpm/next@12.3.3_@babel+core@7.14.3_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { ACTIVITY_EVENTS, AD_VISIBILITY_RATIO, DEBOUNCE_TIME, REFRESH_AD_TIME, USER_INACTIVE_TIME, BidderState, Bidder, AdPlatform, GoogleEventName } from 'constants/ads';
import { ConsentGroup } from 'constants/consent';
import { checkConsentGroup } from 'libs/utils/consentGroups';
import { serverSide } from 'libs/utils/environment';
import { isMobile } from 'libs/utils/userAgentParser';
import { getUserAgent } from 'libs/utils/window';
import { sendConsentedAdserverRequest, fetchAmazonBids, fetchPrebidBids, setupAdPlacement, setupGoogleSlot, setupSasFormat, getPlacementById } from './utils';

const AdManager = (() => {
  let adCount = 0;
  const refreshManager = {
    isOn: false,
    lastActionTimestamp: Date.now(),
    refreshInterval: undefined,
    debounceTimer: undefined
  };
  const locationManager = {
    isLocationChangeEventSetup: false,
    lastHref: serverSide ? '/' : window.location.href
  };
  const requestManager = Object.values(Bidder).reduce((acc, bidder) => _objectSpread(_objectSpread({}, acc), {}, {
    [bidder]: BidderState.Idle
  }), {});
  const individualRequestManager = {};

  function areAllBidsBack(placementId) {
    const bidders = Object.values(Bidder);
    const backStates = [BidderState.Fetched, BidderState.TimedOut, BidderState.BadSizes, BidderState.NoBids];
    return bidders.map(bidder => individualRequestManager[placementId][bidder]).filter(bidderState => backStates.includes(bidderState)).length === bidders.length;
  }

  function headerBidderBack(bidder, placementId, state) {
    if (!individualRequestManager[placementId]) {
      individualRequestManager[placementId] = _objectSpread({}, requestManager);
    }

    individualRequestManager[placementId][bidder] = state;

    if (areAllBidsBack(placementId)) {
      sendConsentedAdserverRequest(placementId, individualRequestManager[placementId]);
    }
  }

  function requestHeaderBidding(id) {
    let refresh = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (checkConsentGroup(ConsentGroup.Targeting)) fetchAmazonBids(id, headerBidderBack, refresh);
    fetchPrebidBids(id, headerBidderBack, refresh);
  }

  function isAdVisible(node) {
    if (!node) return false;
    const boundaries = node.getBoundingClientRect();
    if (!boundaries) return false;
    const offset = boundaries.height * AD_VISIBILITY_RATIO;
    const isVisible = window.innerHeight > boundaries.top + offset && boundaries.bottom > offset;
    return isVisible;
  }

  function isUserActive() {
    const inactiveDuration = (Date.now() - refreshManager.lastActionTimestamp) / 1000;
    return !document.hidden && inactiveDuration < USER_INACTIVE_TIME;
  }

  function refreshAd(placementId) {
    const placement = getPlacementById(placementId);
    if (!placement || !individualRequestManager[placementId]) return;
    individualRequestManager[placementId] = _objectSpread({}, requestManager);
    requestHeaderBidding(placementId, true);
    window.adPlacements[placementId].activeDuration = 0;
  }

  function handleRefreshTick() {
    if (!window.adPlacements) return;
    if (!isUserActive()) return;
    Object.keys(window.adPlacements).filter(placementId => {
      const placement = getPlacementById(placementId);
      if (!placement) return false;
      const {
        isManuallyRefreshed,
        isRefreshEnabled
      } = placement;
      if (isRefreshEnabled && !isManuallyRefreshed) return true;
      return false;
    }).forEach(placementId => {
      const placement = getPlacementById(placementId);
      if (!placement) return;
      const {
        isOutOfPageAd,
        node,
        activeDuration
      } = placement;
      const isRefreshAvailable = isOutOfPageAd || isAdVisible(node);
      if (!isRefreshAvailable) return;

      if (activeDuration >= REFRESH_AD_TIME) {
        refreshAd(placementId);
      }

      window.adPlacements[placementId].activeDuration += 1;
    });
  }

  function handleActivityEvent() {
    refreshManager.lastActionTimestamp = Date.now();
  }

  function handleLocationChange() {
    if (window.location.href !== locationManager.lastHref && !!window.adPlacements && !refreshManager.debounceTimer) {
      refreshManager.debounceTimer = setTimeout(() => {
        refreshManager.debounceTimer = undefined;
      }, DEBOUNCE_TIME);
      Object.keys(window.adPlacements).forEach(placementId => {
        const placement = getPlacementById(placementId);
        if (!placement) return;
        const {
          node,
          isOutOfPageAd
        } = placement;
        if (!isOutOfPageAd && !node) return;
        refreshAd(placementId);
      });
    }

    locationManager.lastHref = window.location.href;
  }

  function setupUrlChangeHandler() {
    if (locationManager.isLocationChangeEventSetup || !('Proxy' in window)) return;
    window.history.pushState = new Proxy(window.history.pushState, {
      apply: (target, thisArg, argArray) => {
        window.dispatchEvent(new Event('locationchange'));
        return target.apply(thisArg, argArray);
      }
    });
    window.addEventListener('locationchange', handleLocationChange);
    locationManager.isLocationChangeEventSetup = true;
  }

  function setupRefresh() {
    refreshManager.isOn = true;
    refreshManager.refreshInterval = setInterval(handleRefreshTick, 1000);
    ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, handleActivityEvent));
    setupUrlChangeHandler();
  }

  function stopAdRefresh() {
    if (refreshManager.refreshInterval) clearInterval(refreshManager.refreshInterval);
    refreshManager.isOn = false;
    ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, handleActivityEvent));
    window.removeEventListener('locationchange', handleLocationChange);
  }

  function generatePlacementId() {
    adCount += 1;
    return "adplacement-".concat(adCount);
  }

  function unregisterPlacement(placementId) {
    const {
      adPlacements
    } = window;
    if (!adPlacements) return;
    const adPlacement = adPlacements[placementId];
    if (!adPlacement) return;
    const {
      sas,
      googletag
    } = window;

    if (googletag && adPlacement.googleSlot) {
      const slot = adPlacement.googleSlot;
      googletag.cmd.push(() => {
        googletag.destroySlots([slot]);
      });
    }

    if (sas) {
      sas.cmd.push(() => {
        sas.clean(placementId);
      });
    }

    delete adPlacements[placementId];
    delete individualRequestManager[placementId];
    if (!Object.keys(adPlacements).length) stopAdRefresh();
  }

  function registerPlacement(_ref) {
    let {
      id,
      placementConfig,
      onPlacementLoad,
      onPlacementRenderEnded,
      onPlacementRequest,
      onImpressionViewable,
      isRefreshEnabled,
      isManuallyRendered = false,
      isManuallyRefreshed = false,
      iframeTitle
    } = _ref;
    const node = document.getElementById(id);
    if (!node) return;
    setupAdPlacement({
      id,
      placementConfig,
      node,
      isManuallyRendered,
      isManuallyRefreshed,
      isRefreshEnabled
    });
    setupGoogleSlot(id, {
      [GoogleEventName.SlotOnload]: onPlacementLoad,
      [GoogleEventName.SlotRequested]: onPlacementRequest,
      [GoogleEventName.SlotRenderEnded]: onPlacementRenderEnded,
      [GoogleEventName.ImpressionViewable]: onImpressionViewable
    }, iframeTitle);
    setupSasFormat(id, placementConfig.shape);
    requestHeaderBidding(id);
    if (!refreshManager.isOn && isRefreshEnabled) setupRefresh();
  }

  function getAdsPlatform() {
    return isMobile(getUserAgent()) ? AdPlatform.Mobile : AdPlatform.Web;
  }

  function sendManualAdRequest(placementId) {
    const placement = getPlacementById(placementId);
    if (!placement) return;
    googletag.cmd.push(() => {
      if (placement.googleSlot) googletag.pubads().refresh([placement.googleSlot]);
    });
  }

  return {
    generatePlacementId,
    unregisterPlacement,
    registerPlacement,
    getAdsPlatform,
    sendManualAdRequest
  };
})();

export default AdManager;